import React, { useState, useEffect, useCallback, useRef, useContext, createContext } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Chip,
  IconButton,
  Snackbar,
  Alert,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import {
  Security,
  Close,
  Public,
  Lock,
  VisibilityOff,
} from "@mui/icons-material";
import { CommitteeContext } from "../AppContext/CommitteeContext";
import { AuthContext } from "../AppContext/AuthContext";
import { saveActionType } from "../Config/db/permission.operations";
import PERMISSION_MAP from "./permissionMap";

/**
 * Context so the AppBar toggle can control Permission Mode state.
 */
export const PermissionModeContext = createContext({
  active: false,
  setActive: () => {},
  isAvailable: false,
});

const TYPE_CONFIG = {
  public:     { color: "#4caf50", label: "Public",     desc: "Anyone in community", icon: <Public fontSize="small" /> },
  restricted: { color: "#ff9800", label: "Restricted", desc: "Committee area owners only", icon: <Lock fontSize="small" /> },
  hidden:     { color: "#f44336", label: "Hidden",     desc: "Invisible to non-owners", icon: <VisibilityOff fontSize="small" /> },
};

const OUTLINE_STYLES = {
  public:     "3px solid #4caf50",
  restricted: "3px solid #ff9800",
  hidden:     "3px solid #f44336",
  unknown:    "3px dashed #9e9e9e",
};

function getButtonLabel(el) {
  if (el.getAttribute("aria-label")) return el.getAttribute("aria-label");
  const text = el.textContent?.trim();
  if (text && text.length < 80) return text;
  if (el.title) return el.title;
  const svg = el.querySelector("svg[data-testid]");
  if (svg) return svg.getAttribute("data-testid").replace("Icon", "");
  return "(button)";
}

/**
 * PermissionMode — overlay that highlights gated buttons and lets
 * committee members change their type (Public / Restricted / Hidden).
 *
 * Toggle is in the AppBar via PermissionModeContext.
 * This component provides the context + renders the overlay/dialog.
 */
export default function PermissionMode({ children }) {
  const { isCommitteeMember, permConfig, getActionType } = useContext(CommitteeContext);
  const { userInfo } = useContext(AuthContext);
  const [active, setActive] = useState(false);
  const [buttons, setButtons] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedActionId, setSelectedActionId] = useState(null);
  const [selectedLabel, setSelectedLabel] = useState("");
  const [snack, setSnack] = useState({ open: false, message: "" });
  const [saving, setSaving] = useState(false);
  const scanRef = useRef(null);

  const scanButtons = useCallback(() => {
    const all = document.querySelectorAll(
      'button, [role="button"], a.MuiButton-root, a.MuiIconButton-root'
    );
    const found = Array.from(all).filter(
      (el) => !el.closest("[data-permission-mode]")
    );
    setButtons(found);
    return found;
  }, []);

  // Scan periodically when active
  useEffect(() => {
    if (active) {
      scanButtons();
      scanRef.current = setInterval(scanButtons, 2000);
      return () => clearInterval(scanRef.current);
    } else {
      setButtons([]);
      clearInterval(scanRef.current);
    }
  }, [active, scanButtons]);

  // Apply outline styles
  useEffect(() => {
    if (!active) {
      document.querySelectorAll("[data-perm-mode]").forEach((el) => {
        el.style.outline = "";
        el.style.outlineOffset = "";
        el.removeAttribute("data-perm-mode");
      });
      return;
    }

    buttons.forEach((el) => {
      const aid = el.getAttribute("data-aid");
      let outline;
      if (aid) {
        const type = getActionType ? getActionType(aid) : "public";
        outline = OUTLINE_STYLES[type] || OUTLINE_STYLES.public;
      } else {
        outline = OUTLINE_STYLES.unknown;
      }
      el.style.outline = outline;
      el.style.outlineOffset = "2px";
      el.setAttribute("data-perm-mode", "true");
    });
  }, [active, buttons, permConfig, getActionType]);

  // Intercept clicks when active
  useEffect(() => {
    if (!active) return;

    const handler = (e) => {
      const btn = e.target.closest(
        'button, [role="button"], a.MuiButton-root, a.MuiIconButton-root'
      );
      if (
        !btn ||
        btn.closest("[data-permission-mode]") ||
        btn.closest(".MuiDialog-root") ||
        btn.closest(".MuiModal-root") ||
        btn.closest(".MuiSnackbar-root")
      ) return;

      e.preventDefault();
      e.stopPropagation();

      const aid = btn.getAttribute("data-aid");
      if (!aid) {
        setSnack({ open: true, message: "This button has no actionId — not gated yet" });
        return;
      }

      setSelectedActionId(aid);
      setSelectedLabel(getButtonLabel(btn));
      setDialogOpen(true);
    };

    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [active]);

  const handleTypeChange = async (newType) => {
    if (!newType || !selectedActionId || !userInfo?.communityCode) return;
    setSaving(true);
    try {
      await saveActionType(userInfo.communityCode, permConfig, selectedActionId, newType);
      setSnack({ open: true, message: `Set "${selectedLabel}" to ${TYPE_CONFIG[newType].label}` });
      setDialogOpen(false);
    } catch (err) {
      console.error("Failed to save:", err);
      setSnack({ open: true, message: "Failed to save" });
    } finally {
      setSaving(false);
    }
  };

  // Stats
  const gatedButtons = buttons.filter((b) => b.getAttribute("data-aid"));
  const ungatedCount = buttons.length - gatedButtons.length;
  const typeCounts = { public: 0, restricted: 0, hidden: 0 };
  gatedButtons.forEach((b) => {
    const type = getActionType ? getActionType(b.getAttribute("data-aid")) : "public";
    if (typeCounts[type] !== undefined) typeCounts[type]++;
  });

  const currentType = selectedActionId && getActionType ? getActionType(selectedActionId) : "public";
  const actionMeta = selectedActionId ? PERMISSION_MAP[selectedActionId] : null;

  // Only show for committee members (guard after all hooks)
  if (!isCommitteeMember) {
    return (
      <PermissionModeContext.Provider value={{ active: false, setActive: () => {}, isAvailable: false }}>
        {children}
      </PermissionModeContext.Provider>
    );
  }

  return (
    <PermissionModeContext.Provider value={{ active, setActive, isAvailable: true }}>
      {children}
      <Box data-permission-mode="true">
        {/* Type Change Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.2)" } } }}
        >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
          <Security color="primary" fontSize="small" />
          Change Button Type
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={() => setDialogOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, p: 1.5, bgcolor: "#f5f5f5", borderRadius: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              "{selectedLabel}"
            </Typography>
            {actionMeta && (
              <Box sx={{ mt: 0.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip label={actionMeta.area} size="small" color="primary" variant="outlined" />
                <Chip label={actionMeta.feature} size="small" variant="outlined" />
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", display: "block", mt: 0.5 }}>
              {selectedActionId}
            </Typography>
          </Box>

          <ToggleButtonGroup
            value={currentType}
            exclusive
            onChange={(e, val) => handleTypeChange(val)}
            disabled={saving}
            fullWidth
            sx={{ mb: 1 }}
          >
            {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
              <ToggleButton
                key={key}
                value={key}
                sx={{
                  flexDirection: "column",
                  py: 1.5,
                  textTransform: "none",
                  "&.Mui-selected": {
                    bgcolor: cfg.color,
                    color: "#fff",
                    "&:hover": { bgcolor: cfg.color },
                  },
                }}
              >
                {cfg.icon}
                <Typography variant="caption" fontWeight="bold" sx={{ mt: 0.5 }}>
                  {cfg.label}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: "0.6rem", opacity: 0.8 }}>
                  {cfg.desc}
                </Typography>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack({ open: false, message: "" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setSnack({ open: false, message: "" })}
        >
          {snack.message}
        </Alert>
      </Snackbar>
      </Box>
    </PermissionModeContext.Provider>
  );
}
